// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateRangeInput } from '@/components/date-range-input';
import { TableControlContainer } from '@/components/table-control-container';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { PersonFilter } from '@/filters/person-filter';
import { ProjectFilter } from '@/filters/project-filter';
import { TimeRecordCategoryFilter } from '@/filters/time-record-category-filter';
import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  debounce,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { ChangeEventHandler, useMemo, useRef } from 'react';

interface Props {
  filter: {
    search?: string;
    startDate?: DateTime | null;
    endDate?: DateTime | null;
    personIds: string[];
    projectIds: string[];
    categories: string[];
  };
  onSearchChange: (search: string) => void;
  onDateRangeChange: (dateRange: DateRange<DateTime>) => void;
  onPersonsChange: (personIds: string[]) => void;
  onProjectsChange: (projectIds: string[]) => void;
  onCategoriesChange: (categories: string[]) => void;
  onClearSearch: () => void;
}

/**
 * Filter controls for the time records table including date range, person, project, category, and search filters.
 */
export function TimeRecordFilterBar(props: Props) {
  const searchField = useRef<HTMLInputElement | null>(null);

  const debouncedSearchChange = useMemo(
    () => debounce(props.onSearchChange, 300),
    [props.onSearchChange]
  );

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    debouncedSearchChange(event.target.value);
  };

  const handleClearSearch = () => {
    const currentSearchField = searchField.current;
    if (currentSearchField) {
      currentSearchField.value = '';
    }
    props.onClearSearch();
  };

  const dateRange: DateRange<DateTime> = [
    props.filter.startDate ?? null,
    props.filter.endDate ?? null,
  ];

  return (
    <TableControlContainer>
      <Stack
        direction="row"
        marginRight="auto"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        <DateRangeInput
          isClearable
          label="Date range"
          value={dateRange}
          width="16rem"
          onChange={props.onDateRangeChange}
        />
        <PersonFilter
          value={props.filter.personIds}
          onChange={props.onPersonsChange}
          label="Person"
          enableNotSet={false}
        />
        <ProjectFilter
          value={props.filter.projectIds}
          onChange={props.onProjectsChange}
          label="Project"
          enableNotSet={true}
        />
        <TimeRecordCategoryFilter
          value={props.filter.categories}
          onChange={props.onCategoriesChange}
          label="Category"
          enableNotSet={false}
        />
      </Stack>
      <TextField
        size="small"
        label="Search time records"
        defaultValue={props.filter.search}
        inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
        onChange={handleSearchChange}
        sx={{ minWidth: 300 }}
        InputProps={{
          inputRef: searchField,
          endAdornment: props.filter.search ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                onClick={handleClearSearch}
                edge="end"
                size="small"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
    </TableControlContainer>
  );
}
